/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2022 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2023-2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023-2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025-2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { Component, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { BsPerson } from 'react-icons/bs';
import { GoSignOut } from 'react-icons/go';
import { IoPersonCircle } from 'react-icons/io5';

import './custom.scss';
import './Menu.css';
import './styles/shared.css';
import './styles/admin-theme.css';

import { Layout } from './components/Layout';
import { Home } from './components/Home';

import MachineList from './MachineList/MachineList';
import SoftwareList from './SoftwareList/SoftwareList';
import FairList from './FairList/FairList';
import UserList from './UserList/UserList';
import CompanyData from './CompanyData/CompanyData';

import Login from 'components/Login';

import NewHome from 'Pages/Home/Home';
import NewMe from 'Pages/Me/Me';
import NewSoftwares from 'Pages/Softwares/Softwares';
import NewFairs from 'Pages/Fairs/Fairs';
import NewMachines from 'Pages/Machines/Machines';
import NewUsers from 'Pages/Users/Users';
import NewCompanies from 'Pages/Companies/Companies';

import Me from 'Me/Me';

import StateToast, { StatesProvider } from './Contexts/StateToast';
import { AuthProvider, ProtectedRoute, useAuth } from 'Contexts/Auth';
import ErrorBoundary from 'Pages/components/ErrorBoundary';

import umatiLogo from './assets/umati_logo.png';

const NEW_BASE = '/new';
const ADMIN_ROLES = ['SuperAdmin', 'Admin'];
const SOFTWARE_ROLES = ['SuperAdmin', 'Admin', 'CompanyMember'];

const hasAnyRole = (roles: string[] | null | undefined, allowed: string[]) =>
  roles?.some((role) => allowed.includes(role)) === true;

const menuLinkClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'menu-link active' : 'menu-link');

const AppLoading = () => <div className="app-loading">Loading…</div>;

const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { authed } = useAuth();
  const location = useLocation();

  if (authed === undefined) return <AppLoading />;
  if (!authed) {
    return <Navigate to={`${NEW_BASE}/login?path=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
};

const Links = () => {
  const { authed, user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navRef = useRef<HTMLElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const roles = user?.Roles;
  const isAdmin = hasAnyRole(roles, ADMIN_ROLES);
  const canSeeSoftware = hasAnyRole(roles, SOFTWARE_ROLES);
  const isMeActive = location.pathname === `${NEW_BASE}/Me`;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const activeLink = nav.querySelector<HTMLElement>('.menu-link.active');
    if (!activeLink) {
      nav.style.setProperty('--tab-width', '0px');
      return;
    }

    const navRect = nav.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();
    nav.style.setProperty('--tab-left', `${linkRect.left - navRect.left}px`);
    nav.style.setProperty('--tab-width', `${linkRect.width}px`);
  }, [location.pathname, authed, isAdmin, canSeeSoftware]);

  return (
    <nav className="menu" ref={navRef} aria-label="Main navigation">
      <ul className="menu-list">
        <li className="menu-logo-area">
          <NavLink to={`${NEW_BASE}/home`} end className="menu-brand-link" aria-label="umati Admin home">
            <div className="menu-brand">
              <img src={umatiLogo} alt="umati" className="menu-logo-img" />
              <span className="menu-context-label">ADMIN</span>
            </div>
          </NavLink>
        </li>

        {authed && (
          <>
            <li>
              <NavLink to={`${NEW_BASE}/Machines`} className={menuLinkClass}>
                <span>Machines</span>
              </NavLink>
            </li>

            <li>
              <NavLink to={`${NEW_BASE}/Companies`} className={menuLinkClass}>
                <span>Companies</span>
              </NavLink>
            </li>

            {isAdmin && (
              <li>
                <NavLink to={`${NEW_BASE}/Users`} className={menuLinkClass}>
                  <span>Users</span>
                </NavLink>
              </li>
            )}

            {canSeeSoftware && (
              <li>
                <NavLink to={`${NEW_BASE}/Software`} className={menuLinkClass}>
                  <span>Software</span>
                </NavLink>
              </li>
            )}

            {isAdmin && (
              <li>
                <NavLink to={`${NEW_BASE}/Fairs`} className={menuLinkClass}>
                  <span>Fairs</span>
                </NavLink>
              </li>
            )}
          </>
        )}

        <li className="menu-user-area">
          {authed === false && (
            <NavLink to={`${NEW_BASE}/login`} className={menuLinkClass}>
              Login
            </NavLink>
          )}

          {authed && (
            <div className="user-menu" ref={userMenuRef}>
              <button
                type="button"
                className="user-icon"
                aria-haspopup="true"
                aria-expanded={open}
                aria-label="User menu"
                onClick={() => setOpen((prev) => !prev)}
              >
                <IoPersonCircle className={isMeActive || open ? 'active' : ''} />
              </button>

              {open && (
                <div className="user-dropdown">
                  <NavLink
                    to={`${NEW_BASE}/Me`}
                    className={({ isActive }) => (isActive ? 'dropdown-link active' : 'dropdown-link')}
                  >
                    <BsPerson />
                    <span>My Account</span>
                  </NavLink>
                  <button
                    type="button"
                    className="dropdown-link"
                    onClick={async () => {
                      setOpen(false);
                      navigate(`${NEW_BASE}/login`, { replace: true });
                      await logout();
                    }}
                  >
                    <GoSignOut />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </li>
      </ul>
    </nav>
  );
};

const RootRedirect = () => {
  const { authed } = useAuth();

  if (authed === undefined) return <AppLoading />;
  if (authed === false) return <Navigate to={`${NEW_BASE}/login`} replace />;

  return <Navigate to={`${NEW_BASE}/home`} replace />;
};

const NewApp = () => {
  const location = useLocation();

  useEffect(() => {
    document.body.classList.add('new-admin');
    return () => document.body.classList.remove('new-admin');
  }, []);

  return (
    <div className="new-app">
      <header className="app-header">
        <Links />
      </header>
      <main className="new-app-main">
        <ErrorBoundary resetKey={location.pathname}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="login" element={<Login />} />
            <Route
              path="home"
              element={
                <RequireAuth>
                  <NewHome />
                </RequireAuth>
              }
            />
            <Route
              path="Me"
              element={
                <RequireAuth>
                  <NewMe />
                </RequireAuth>
              }
            />
            <Route
              path="Software"
              element={
                <RequireAuth>
                  <NewSoftwares />
                </RequireAuth>
              }
            />
            <Route path="Softwares" element={<Navigate to={`${NEW_BASE}/Software`} replace />} />
            <Route
              path="Machines"
              element={
                <RequireAuth>
                  <NewMachines />
                </RequireAuth>
              }
            />
            <Route
              path="Fairs"
              element={
                <RequireAuth>
                  <NewFairs />
                </RequireAuth>
              }
            />
            <Route
              path="Users"
              element={
                <RequireAuth>
                  <NewUsers />
                </RequireAuth>
              }
            />
            <Route
              path="Companies"
              element={
                <RequireAuth>
                  <NewCompanies />
                </RequireAuth>
              }
            />
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
};

const OldApp = () => (
  <Layout>
    <>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route path="login" element={<Login />} />
        <Route
          path="Me"
          element={
            <ProtectedRoute>
              <Me />
            </ProtectedRoute>
          }
        />
        <Route
          path="MachineList"
          element={
            <ProtectedRoute>
              <MachineList />
            </ProtectedRoute>
          }
        />
        <Route
          path="Softwares"
          element={
            <ProtectedRoute>
              <SoftwareList />
            </ProtectedRoute>
          }
        />
        <Route
          path="Fairs"
          element={
            <ProtectedRoute>
              <FairList />
            </ProtectedRoute>
          }
        />
        <Route
          path="Users"
          element={
            <ProtectedRoute>
              <UserList />
            </ProtectedRoute>
          }
        />
        <Route
          path="CompanyData"
          element={
            <ProtectedRoute>
              <CompanyData />
            </ProtectedRoute>
          }
        />
      </Routes>
      <StateToast />
    </>
  </Layout>
);

export default class App extends Component {
  static displayName = App.name;

  render() {
    return (
      <StatesProvider>
        <AuthProvider>
          <Routes>
            <Route path="/new/*" element={<NewApp />} />
            <Route path="/*" element={<OldApp />} />
          </Routes>
        </AuthProvider>
      </StatesProvider>
    );
  }
}
